import React, { useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import DetailModal from './DetailModal';

// === The 3 real upcoming KBT events ====================================
// Parshat Matot-Masei week — Shabbat July 11, 2026. The flyer JPGs live in
// /public/assets/images/kbt/. Cards render only the image (no overlay
// text) and open the full flyer in the detail modal on click.
const EVENTS = [
  {
    id: 'rabbi-hanoch-teller',
    image: '/assets/images/kbt/flyer-rabbi-hanoch-teller-jul12.jpg',
    palette: {
      bg: 'linear-gradient(160deg, #1F2F5C 0%, #0E1A3E 70%, #07112B 100%)',
      tint: '#E2C778',
      sub: '#D4B26B',
      ink: '#FBF8F1',
    },
    title: 'Rabbi Hanoch Teller',
    eyebrow: 'Sun, Jul 12  ·  From Jerusalem',
    feature: 'Topic',
    name: '“If You Had Only One Message to Hear”',
    nameNote: 'An inspiring morning with',
    timeLabel: 'Time',
    time: '9:15 AM',
    extra: 'Breakfast will be served',
    footer: 'KBT  ·  9017 W. Pico Blvd',
    detail: {
      kicker: 'Sunday Morning  ·  July 12, 2026',
      hebrew: '',
      title: 'Rabbi Hanoch Teller',
      subtitle: '“If You Had Only One Message to Hear” — from Jerusalem, the King of the Storytellers.',
      time: '9:15 AM',
      body: [
        'Rabbi Hanoch Teller is a globe-trotting modern-day maggid renowned as the King of the Storytellers and has been labeled as ‘the Shakespeare of the Yeshiva World’ (The Jerusalem Post). He has lectured before audiences on five continents, in 40 American States and 24 other countries.',
        'For four decades Rabbi Teller has been a sought-after tour guide at the Yad Vashem Holocaust Museum in Jerusalem — his Holocaust expertise, deep humanity, and uncanny ability to tell just the right story at the right time transform the infinite banality of horror into an intensely personal encounter with the Destruction of European Jewry.',
        'Breakfast will be served. Join us for an inspiring morning.',
      ],
      meta: [
        { label: 'When',    value: 'Sunday, July 12, 2026' },
        { label: 'Time',    value: '9:15 AM' },
        { label: 'Topic',   value: 'If You Had Only One Message to Hear' },
        { label: 'Speaker', value: 'Rabbi Hanoch Teller, from Jerusalem' },
        { label: 'Includes', value: 'Breakfast' },
        { label: 'Location', value: 'KBT  ·  9017 W. Pico Blvd, Los Angeles, CA 90035' },
      ],
      accent: '#E2C778',
    },
  },
  {
    id: 'ladies-shiur-jul11',
    image: '/assets/images/kbt/flyer-ladies-shiur-jul11.jpg',
    palette: {
      bg: 'linear-gradient(160deg, #FBEFF3 0%, #F3DCE6 55%, #E7C3D4 100%)',
      tint: '#8C1D5C',
      sub: '#A64577',
      ink: '#3A0F26',
    },
    title: 'Ladies Shiur',
    eyebrow: 'Jul 11  ·  Shabbat Morning',
    feature: 'This week’s speaker',
    name: 'Mrs. Rachel Farivar',
    nameNote: 'Just for ladies',
    timeLabel: 'Time',
    time: '10:45 AM',
    extra: 'KBT  ·  9017 W. Pico Blvd',
    footer: 'Come be uplifted, inspired, and strengthened together',
    detail: {
      kicker: 'Ladies Shiur  ·  Shabbat Morning, July 11, 2026',
      hebrew: 'שִׁעוּר נָשִׁים',
      title: 'Ladies Shiur',
      subtitle: 'This week’s speaker: Mrs. Rachel Farivar. An inspiring & empowering shiur — just for ladies.',
      time: '10:45 AM',
      body: [
        'Join us for an inspiring & empowering shiur, just for ladies.',
        'This week’s speaker: Mrs. Rachel Farivar.',
        'Come be uplifted, inspired, and strengthened together. We look forward to seeing you.',
      ],
      meta: [
        { label: 'When',    value: 'Shabbat Morning, July 11, 2026' },
        { label: 'Time',    value: '10:45 AM' },
        { label: 'Speaker', value: 'Mrs. Rachel Farivar' },
        { label: 'For',     value: 'Women of the community' },
        { label: 'Location', value: 'KBT  ·  9017 W. Pico Blvd' },
      ],
      accent: '#8C1D5C',
    },
  },
  {
    id: 'thursday-nights',
    image: '/assets/images/kbt/flyer-thursday-nights.jpg',
    palette: {
      bg: 'linear-gradient(160deg, #E4EEF8 0%, #CFE0F1 55%, #B7CFE8 100%)',
      tint: '#B05A0F',
      sub: '#C97A1F',
      ink: '#1B2A3D',
    },
    title: 'Thursday Nights',
    eyebrow: 'Thursdays  ·  Weekly',
    feature: 'Insights on Parasha',
    name: 'Rabbi Dror Baalhaness',
    nameNote: null,
    timeLabel: 'Time',
    time: '8:30 PM',
    extra: 'Followed by Mishmar',
    footer: 'KBT  ·  9017 W. Pico Blvd',
    detail: {
      kicker: 'Thursday Nights  ·  Weekly',
      hebrew: '',
      title: 'Thursday Nights  ·  Insights on Parasha',
      subtitle: 'With Rabbi Dror Baalhaness, followed by Mishmar.',
      time: '8:30 PM',
      body: [
        'Join Rabbi Dror Baalhaness every Thursday night for insights on the weekly parasha.',
        'Followed by Mishmar.',
      ],
      meta: [
        { label: 'When',    value: 'Thursdays, weekly' },
        { label: 'Time',    value: '8:30 PM' },
        { label: 'Speaker', value: 'Rabbi Dror Baalhaness' },
        { label: 'Followed by', value: 'Mishmar' },
        { label: 'Location', value: 'KBT  ·  9017 W. Pico Blvd' },
      ],
      accent: '#B05A0F',
    },
  },
];

function FlyerCard({ event, index, progress, onOpen }) {
  const ref = useRef(null);
  const y = useTransform(progress, [0, 1], [40, -40]);
  const { palette } = event;

  return (
    <motion.div
      ref={ref}
      style={{ y }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-10% 0%' }}
      transition={{ duration: 1, delay: 0.15 * index, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        onClick={() => onOpen(event)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(event)}
        aria-label={event.title}
        sx={{
          cursor: 'pointer',
          position: 'relative',
          aspectRatio: { xs: '3/4', md: '3/4.2' },
          background: palette.bg,
          backgroundImage: `url('${event.image}')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          overflow: 'hidden',
          transition: 'transform 500ms cubic-bezier(0.22,1,0.36,1), box-shadow 500ms ease',
          boxShadow: '0 20px 40px -24px rgba(7,6,26,0.45)',
          '&:hover': {
            transform: 'translateY(-6px) scale(1.01)',
            boxShadow: '0 30px 60px -30px rgba(7,6,26,0.55)',
          },
          '&:focus-visible': { outline: `2px solid ${palette.tint}`, outlineOffset: 4 },
        }}
      />
    </motion.div>
  );
}

export default function FeaturedFlyers() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const headY = useTransform(scrollYProgress, [0, 0.5], [50, -20]);
  const [openEvent, setOpenEvent] = useState(null);

  return (
    <Box
      ref={ref}
      component="section"
      id="featured"
      sx={{
        position: 'relative',
        py: { xs: 14, md: 24 },
        px: { xs: 3, md: 6 },
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        spacing={3}
        sx={{ mb: { xs: 8, md: 12 }, maxWidth: 1320, mx: 'auto' }}
      >
        <Box sx={{ maxWidth: 760 }}>
          <Box className="kicker" sx={{ mb: 2 }}>This Week at KBT</Box>
          <motion.h2 style={{ y: headY, margin: 0 }}>
            <Box
              sx={{
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: 'clamp(40px, 6vw, 100px)',
                lineHeight: 0.94,
                letterSpacing: '-0.025em',
                color: 'var(--navy-500)',
              }}
            >
              Featured,
              <span style={{ fontWeight: 500, fontStyle: 'italic', color: 'var(--teal-500)' }}>
                {' '}three to know.
              </span>
            </Box>
          </motion.h2>
        </Box>
        <Box
          sx={{
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontStyle: 'italic',
            fontSize: 'clamp(16px, 1.5vw, 20px)',
            color: 'var(--text-muted)',
            maxWidth: 360,
            lineHeight: 1.5,
          }}
        >
          Click a flyer to see the full details — date, speaker, time, cost.
          RSVP for the workshop required.
        </Box>
      </Stack>

      <Box
        sx={{
          maxWidth: 1320,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: { xs: 4, md: 4 },
        }}
      >
        {EVENTS.map((e, i) => (
          <FlyerCard
            key={e.id}
            event={e}
            index={i}
            progress={scrollYProgress}
            onOpen={setOpenEvent}
          />
        ))}
      </Box>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={3}
        sx={{ mt: { xs: 8, md: 12 }, maxWidth: 1320, mx: 'auto' }}
      >
        <Box
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            letterSpacing: '0.22em',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
          }}
        >
          {EVENTS.length}  ·  Featured  ·  this week
        </Box>

        <Box
          onClick={() => navigate('/events')}
          sx={{
            cursor: 'pointer',
            px: 3.5,
            py: 1.5,
            background: 'var(--navy-500)',
            color: '#fff',
            fontFamily: 'var(--font-body)',
            fontWeight: 700,
            fontSize: 11.5,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            borderRadius: 999,
            transition: 'background 250ms ease, transform 250ms ease',
            '&:hover': { background: 'var(--navy-700)', transform: 'translateY(-2px)' },
          }}
        >
          See all events
        </Box>
      </Stack>

      <DetailModal
        open={Boolean(openEvent)}
        onClose={() => setOpenEvent(null)}
        accent={openEvent?.detail?.accent}
        kicker={openEvent?.detail?.kicker}
        hebrew={openEvent?.detail?.hebrew}
        title={openEvent?.detail?.title}
        subtitle={openEvent?.detail?.subtitle}
        time={openEvent?.detail?.time}
        image={openEvent?.image}
        body={openEvent?.detail?.body}
        meta={openEvent?.detail?.meta}
        cta={
          openEvent?.id === 'mother-daughter-shavuot'
            ? { label: 'RSVP', onClick: () => navigate('/events') }
            : { label: 'See all events', onClick: () => navigate('/events') }
        }
      />
    </Box>
  );
}
